import reactUpdate, {Spec} from 'immutability-helper';

import BenchlingConfig from 'BenchlingConfig';
import {kv} from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import {NUEModule, NUEModules, NUEModuleState} from 'constants/OnboardingConstants';
import {ReviewSummary} from 'constants/SourceConstants';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import localStorage from 'LocalStorage';
import {LOCAL_STORAGE_MANUAL_DISMISSAL_KEYS, TaskStatus} from 'onboarding/OnboardingConstants';
import {Organization} from 'orgs/OrganizationConstants';
import preferences from 'Preferences';

export interface HomeDashboardStoreState {
  entryReviewSummary: ReviewSummary | null;
  worksheetReviewSummary: ReviewSummary | null;
  templateReviewSummary: ReviewSummary | null;
  extraSpaceTasks?: Array<TaskStatus>;
  searchOrgModalState: SearchOrgModalState;
  openModal: string | null;
}

interface SearchOrgModalState {
  searchResults: Array<Organization>;
}

function collapsedPreferenceKey(module: NUEModule): string {
  return `onboarding.minimized_nue_${module}_module`;
}

class HomeDashboardStore extends Store {
  CRISPR_MODAL_KEY!: string;
  SEARCH_ORGANIZATION_MODAL_KEY!: string;
  entryReviewSummary!: ReviewSummary | null;
  worksheetReviewSummary!: ReviewSummary | null;
  templateReviewSummary!: ReviewSummary | null;
  entryReviewSummaryFetchFailed!: boolean;
  worksheetReviewSummaryFetchFailed!: boolean;
  templateReviewSummaryFetchFailed!: boolean;
  extraSpaceTasks?: Array<TaskStatus>;
  nueModules!: NUEModules | null;

  hasGettingStartedSpace!: boolean | null;
  openModal!: string | null;

  searchOrgModalState!: SearchOrgModalState;
  _orgSearchTime!: number | null;

  static override initClass(): void {
    // Modal constants
    this.prototype.CRISPR_MODAL_KEY = 'CRISPRModal';
    this.prototype.SEARCH_ORGANIZATION_MODAL_KEY = 'SearchOrganizationModal';
  }

  override resetState(): void {
    this.entryReviewSummary = null;
    this.worksheetReviewSummary = null;
    this.templateReviewSummary = null;
    this.entryReviewSummaryFetchFailed = false;
    this.worksheetReviewSummaryFetchFailed = false;
    this.templateReviewSummaryFetchFailed = false;
    this.nueModules = null;

    this.hasGettingStartedSpace = null;
    this.openModal = null;

    this.searchOrgModalState = this.defaultSearchOrgModalState();
    this._orgSearchTime = null;
  }

  getState(): HomeDashboardStoreState {
    return {
      entryReviewSummary: this.entryReviewSummary,
      worksheetReviewSummary: this.worksheetReviewSummary,
      templateReviewSummary: this.templateReviewSummary,
      extraSpaceTasks: this.extraSpaceTasks,
      searchOrgModalState: this.searchOrgModalState,
      openModal: this.openModal,
    };
  }

  getNUEModules(): NUEModules | null {
    return this.nueModules;
  }

  getNUEModuleState(module: NUEModule): NUEModuleState | null | undefined {
    if (this.nueModules == null || !BenchlingConfig.ENABLE_NUE_MODULES) {
      return null;
    }
    return this.nueModules[module];
  }

  getEntryReviewSummary(): ReviewSummary | null {
    return this.entryReviewSummary;
  }

  getWorksheetReviewSummary(): ReviewSummary | null {
    return this.worksheetReviewSummary;
  }

  getTemplateReviewSummary(): ReviewSummary | null {
    return this.templateReviewSummary;
  }

  getEntryReviewSummaryFetchFailed(): boolean {
    return this.entryReviewSummaryFetchFailed;
  }

  getWorksheetReviewSummaryFetchFailed(): boolean {
    return this.worksheetReviewSummaryFetchFailed;
  }

  getTemplateReviewSummaryFetchFailed(): boolean {
    return this.templateReviewSummaryFetchFailed;
  }

  getHasGettingStartedSpace(): boolean | null {
    return this.hasGettingStartedSpace;
  }

  defaultSearchOrgModalState(): SearchOrgModalState {
    return {
      searchResults: [],
    };
  }

  updateCollapsed(module: NUEModule, collapsed: boolean): void {
    this.nueModules = reactUpdate(this.nueModules, kv(module, {$merge: {collapsed}}));
    preferences.set(collapsedPreferenceKey(module), collapsed);
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.HOME_DASHBOARD_FETCH_ENTRY_REVIEW_SUMMARY_COMPLETED:
        this.entryReviewSummary = action.entryReviewSummary;
        this.entryReviewSummaryFetchFailed = false;
        this.emitChange();
        break;

      case EventConstants.HOME_DASHBOARD_FETCH_ENTRY_REVIEW_SUMMARY_FAILED:
        this.entryReviewSummaryFetchFailed = true;
        this.emitChange();
        break;

      case EventConstants.HOME_DASHBOARD_FETCH_WORKSHEET_REVIEW_SUMMARY_COMPLETED:
        this.worksheetReviewSummary = action.worksheetReviewSummary;
        this.worksheetReviewSummaryFetchFailed = false;
        this.emitChange();
        break;

      case EventConstants.HOME_DASHBOARD_FETCH_WORKSHEET_REVIEW_SUMMARY_FAILED:
        this.worksheetReviewSummaryFetchFailed = true;
        this.emitChange();
        break;

      case EventConstants.HOME_DASHBOARD_FETCH_TEMPLATE_REVIEW_SUMMARY_COMPLETED:
        this.templateReviewSummary = action.templateReviewSummary;
        this.templateReviewSummaryFetchFailed = false;
        this.emitChange();
        break;

      case EventConstants.HOME_DASHBOARD_FETCH_TEMPLATE_REVIEW_SUMMARY_FAILED:
        this.templateReviewSummaryFetchFailed = true;
        this.emitChange();
        break;

      case EventConstants.ONBOARDING_ACKNOWLEDGE_COMPLETED_MODULE:
        this.nueModules = reactUpdate(
          this.nueModules,
          kv(action.module, {$merge: {acknowledgedCompleted: true}})
        );
        this.emitChange();
        break;

      case EventConstants.ONBOARDING_DISMISS_NUE_MODULE:
        this.nueModules = reactUpdate(this.nueModules, kv(action.module, {$merge: {dismissed: true}}));
        if (LOCAL_STORAGE_MANUAL_DISMISSAL_KEYS[action.module]) {
          localStorage.json.setItem(LOCAL_STORAGE_MANUAL_DISMISSAL_KEYS[action.module], true);
        }

        this.emitChange();
        break;

      case EventConstants.ONBOARDING_RESTORE_NUE_MODULE:
        this.nueModules = reactUpdate(this.nueModules, kv(action.module, {$merge: {dismissed: false}}));
        if (LOCAL_STORAGE_MANUAL_DISMISSAL_KEYS[action.module]) {
          localStorage.json.setItem(LOCAL_STORAGE_MANUAL_DISMISSAL_KEYS[action.module], false);
        }

        this.emitChange();
        break;

      case EventConstants.ONBOARDING_FETCH_GETTING_STARTED_TASKS_COMPLETED: {
        const {modules, hasSpace} = action.gettingStarted as {
          modules: NUEModules;
          hasSpace: boolean;
        };
        let openIndex: NUEModuleState['openIndex'];
        this.hasGettingStartedSpace = hasSpace;

        if (this.nueModules == null) {
          this.nueModules = {};
        }
        const updateModules: Spec<NUEModules> = {};
        if (modules) {
          for (const module of Object.keys(modules) as Array<NUEModule>) {
            if (this.nueModules[module] == null) {
              // Open the first uncompleted task
              openIndex = modules[module]!.tasks!.findIndex(({completed}) => !completed);
              if (openIndex === -1) {
                checkSoftInvariant(modules[module]!.completed!, {
                  message: 'Module was not completed, but all tasks were completed.',
                  expirationDate: '2026-01-31',
                  errorInDevAndTest: true,
                });
                openIndex = null;
              } else if (module === NUEModule.ENTRY || module === NUEModule.SEQUENCE) {
                // Account for the introductory checklist item rendered before these modules' server tasks.
                openIndex += 1;
              }
            } else {
              openIndex = null;
            }

            const oldModule =
              this.nueModules[module] ??
              ({
                collapsed: preferences.get(collapsedPreferenceKey(module)) ?? false,
                openIndex,
                acknowledgedCompleted: modules[module]!.completed!,
                dismissed: localStorage.json.getItem(LOCAL_STORAGE_MANUAL_DISMISSAL_KEYS[module]),
                tasks: null,
              } as NUEModuleState);

            const newModule = reactUpdate(oldModule, {$merge: modules[module]!});
            updateModules[module] = {$set: newModule};
          }
        }

        this.nueModules = reactUpdate(this.nueModules, updateModules);
        this.emitChange();
        break;
      }

      case EventConstants.ONBOARDING_TOGGLE_COLLAPSED_COMPLETED_NUE_MODULE:
        this.updateCollapsed(action.module, action.collapsed);
        this.emitChange();
        break;

      case EventConstants.ONBOARDING_COMPLETE_TASK: {
        if (this.nueModules == null) {
          // If we haven't loaded nueModules yet, don't worry about adding it to the store.
          // This can technically cause problems if the nueModule request is on its way back
          // without the new completed task, but this should be rare and not affect users.
          return false;
        }

        const {module, key} = action as unknown as {module: NUEModule; key: string};
        const index = this.nueModules[module]!.tasks!.findIndex((t) => t.key === key);
        checkSoftInvariant(index >= 0, {
          message: `Invalid onboarding task, with module=${module} and key=${key}`,
          expirationDate: '2026-01-31',
          errorInDevAndTest: true,
        });
        const updateTask = {$merge: {completed: true}};
        const updateModule = {tasks: kv(index, updateTask)};
        this.nueModules = reactUpdate(this.nueModules, kv(module, updateModule));

        const tasks = this.nueModules[module]!.tasks!;
        if (tasks.every(({completed}) => completed)) {
          this.nueModules = reactUpdate(
            this.nueModules,
            kv(module, {$merge: {completed: true, openIndex: tasks.length}})
          );
        }
        this.emitChange();
        break;
      }

      case EventConstants.ONBOARDING_OPEN_NUE_MODULE_INDEX: {
        const {module, openIndex} = action as unknown as {module: NUEModule; openIndex: number};
        this.nueModules = reactUpdate(this.nueModules, kv(module, {$merge: {openIndex}}));

        // If we're opening a step, the module should not be collapsed.
        if (openIndex != null && this.nueModules![module]?.collapsed) {
          this.updateCollapsed(module, false);
        }
        this.emitChange();
        break;
      }

      case EventConstants.ONBOARDING_FETCH_EXTRA_SPACE_TASKS_COMPLETED:
        this.extraSpaceTasks = action.extraSpaceTasks;
        this.emitChange();
        break;

      case EventConstants.OPEN_SEARCH_ORG_MODAL:
        this.openModal = this.SEARCH_ORGANIZATION_MODAL_KEY;
        this.emitChange();
        break;

      case EventConstants.CLOSE_SEARCH_ORG_MODAL:
        this.openModal = null;
        this.emitChange();
        break;

      case EventConstants.CLOSE_CRISPR_MODAL:
        this.openModal = null;
        this.emitChange();
        break;

      case EventConstants.RESET_SEARCH_ORG_MODAL_STATE:
        this.searchOrgModalState = this.defaultSearchOrgModalState();
        this.emitChange();
        break;

      case EventConstants.ORG_SEARCH_COMPLETED: {
        const {results, searchTime} = action;
        if (this._orgSearchTime == null || this._orgSearchTime < searchTime) {
          this.searchOrgModalState = reactUpdate(this.searchOrgModalState, {
            searchResults: {$set: results},
          });
          this._orgSearchTime = searchTime;
        }
        this.emitChange();
        break;
      }

      case EventConstants.ORG_SEARCH_FAILED:
        this.searchOrgModalState = reactUpdate(this.searchOrgModalState, {searchResults: {$set: []}});
        this.emitChange();
        break;
      default:
        break;
    }

    return true;
  }
}
HomeDashboardStore.initClass();

export default new HomeDashboardStore(AppDispatcher);
